<script setup lang="ts">
/* The status of one booking, big enough to be the first thing read on its
   page: the state as a coloured band, one sentence on what it means for the
   reader, and the actions that state allows. The page decides the sentence
   and the actions -- they depend on which side of the booking the reader is. */
import Icon from '@/components/Icon.vue'
import type { IconName } from '@/components/icons'
import { bookingStateLabel, bookingStateTone, type BookingState } from '@/lib/bookingLabels'

defineProps<{
  state: BookingState
  explanation: string
  /** Overrides the standard state label, e.g. "Awaiting your answer" for the owner. */
  label?: string
}>()

const ICONS: Record<BookingState, IconName> = {
  requested: 'clock',
  confirmed: 'check',
  completed: 'check',
  rejected: 'close',
  cancelled: 'close',
}
</script>

<template>
  <section class="card" :class="bookingStateTone(state)" aria-label="Booking status">
    <span class="icon"><Icon :name="ICONS[state]" :size="20" /></span>
    <div class="text">
      <p class="label">{{ label ?? bookingStateLabel(state) }}</p>
      <p class="explanation">{{ explanation }}</p>
    </div>
    <div v-if="$slots.actions" class="actions"><slot name="actions" /></div>
  </section>
</template>

<style scoped>
.card {
  --tone: var(--color-text-muted);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background-color: color-mix(in srgb, var(--tone) 8%, var(--color-surface));
  border: var(--border-width) solid color-mix(in srgb, var(--tone) 35%, transparent);
  border-radius: var(--radius-md);
}

.success {
  --tone: var(--color-success);
}

.warning {
  --tone: var(--color-warning);
}

.danger {
  --tone: var(--color-danger);
}

.icon {
  display: grid;
  place-items: center;
  flex: none;
  width: 2.5rem;
  height: 2.5rem;
  color: var(--color-surface);
  background-color: var(--tone);
  border-radius: var(--radius-full);
}

.text {
  flex: 1 1 16rem;
  min-width: 0;
}

.label {
  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  color: var(--color-heading);
}

.explanation {
  margin-top: 2px;
  font-size: var(--text-sm);
  color: var(--color-text);
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2xs);
}

@media (max-width: 40rem) {
  .card {
    padding: var(--space-sm);
  }

  .actions,
  .actions :slotted(*) {
    flex: 1 1 auto;
  }
}
</style>
